import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Eye, Calendar, User, Bookmark, BookmarkCheck, Share2, ExternalLink } from 'lucide-react';

function ArticlePage({ user, token, openAuthModal }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imgError, setImgError] = useState(false);

  const placeholderImage = `https://images.unsplash.com/photo-1511512578047-dfb367046420?w=1200&h=600&fit=crop`;

  useEffect(() => {
    let hasIncremented = false;
    
    const loadArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles/${id}`);
        setArticle(response.data);
      } catch (error) {
        console.error('Error loading article:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (!hasIncremented) {
      loadArticle();
      hasIncremented = true;
    }
    
    if (token) {
      checkIfSaved();
    }
  }, [id, token]);

  const checkIfSaved = async () => {
    try {
      const response = await axios.get(`${API}/user/saved-article-ids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsSaved(response.data.includes(parseInt(id)));
    } catch (error) {
      console.error('Error checking saved status:', error);
    }
  };

  const handleToggleSave = async () => {
    if (!token) {
      openAuthModal('login');
      return;
    }

    setSaving(true);
    try {
      if (isSaved) {
        await axios.delete(`${API}/articles/${id}/save`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsSaved(false);
      } else {
        await axios.post(`${API}/articles/${id}/save`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-4">Article not found</h2>
        <Link to="/" className="text-primary hover:underline">
          Go back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="article-page">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 hover:text-primary transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            
            <div className="flex items-center gap-2">
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-accent transition-colors"
                data-testid="open-original-button"
              >
                <ExternalLink className="w-5 h-5" />
                <span className="hidden sm:inline">Open Original</span>
              </a>
              <button
                onClick={handleShare}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
                data-testid="share-button"
              >
                <Share2 className="w-5 h-5" />
              </button>
              <button
                onClick={handleToggleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-accent transition-colors"
                data-testid="article-save-button"
              >
                {isSaved ? (
                  <>
                    <BookmarkCheck className="w-5 h-5 text-primary" />
                    <span className="hidden sm:inline">Saved</span>
                  </>
                ) : (
                  <>
                    <Bookmark className="w-5 h-5" />
                    <span className="hidden sm:inline">Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Category Badge */}
        <div className="mb-4">
          <span className="inline-block px-3 py-1 text-sm font-semibold bg-primary text-primary-foreground rounded-full">
            {article.category}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-5xl font-bold mb-6" data-testid="article-title">
          {article.title}
        </h1>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6 pb-6 border-b border-border">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{article.publisher_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(article.published_at)}</span>
          </div>
        </div>

        {/* Featured Image */}
        {(article.image_url || !imgError) && (
          <div className="mb-6 rounded-lg overflow-hidden bg-muted">
            <img 
              src={imgError || !article.image_url ? placeholderImage : article.image_url}
              alt={article.title}
              className="w-full h-auto"
              onError={() => setImgError(true)}
              loading="lazy"
            />
          </div>
        )}

        {/* Description */}
        <div className="prose prose-lg max-w-none mb-8">
          <p className="text-xl leading-relaxed">
            {article.description}
          </p>
        </div>

        {/* Article Content in Iframe */}
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="article-content">
          <div className="bg-accent px-4 py-2 text-sm font-medium border-b border-border">
            Full Article from {article.publisher_name}
          </div>
          <iframe
            src={article.url}
            className="w-full"
            style={{ height: '800px', minHeight: '600px' }}
            title={article.title}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
}

export default ArticlePage;